import type { CssVars } from '../../css-vars';
import type { Device, Responsive, Side } from '../../utils';
import { DEVICE_SUFFIX, SIDES, atDevice } from '../../utils';

/**
 * Structural rather than the mixin's own type, so a host still holding a legacy
 * shape — any unit string, sides possibly missing — formats it the same way.
 */
export type SpacingLike = { value?: Partial<Record<Side, number>>; unit?: string };

/** One side, `0px` when unset. */
export function spacingSideVar(spacing: SpacingLike | null | undefined, side: Side): string {
    return `${spacing?.value?.[side] ?? 0}${spacing?.unit ?? 'px'}`;
}

/** One side of a per-breakpoint spacing. */
export function responsiveSpacingSideVar(
    spacing: Responsive<SpacingLike> | null | undefined,
    side: Side,
    device: Device = 'desktop'
): string {
    return spacingSideVar(atDevice(spacing, device), side);
}

/** The `top right bottom left` shorthand, or `undefined` when there is no spacing. */
export function spacingShorthandVar(spacing: SpacingLike | null | undefined): string | undefined {
    return spacing ? SIDES.map((side) => spacingSideVar(spacing, side)).join(' ') : undefined;
}

/** `--<name>-<side><suffix>` for the four sides. */
export function spacingVars(spacing: SpacingLike | null | undefined, name: string, suffix = ''): CssVars {
    return Object.fromEntries(SIDES.map((side) => [`--${name}-${side}${suffix}`, spacingSideVar(spacing, side)]));
}

/** The same, for one breakpoint of a per-breakpoint spacing — `-tablet` / `-mobile` twins. */
export function responsiveSpacingVars(
    spacing: Responsive<SpacingLike> | null | undefined,
    name: string,
    device: Device = 'desktop'
): CssVars {
    return spacingVars(atDevice(spacing, device), name, DEVICE_SUFFIX[device]);
}
